import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ApiService, UserListItem } from '../../../shared/services/api.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthService } from '../../../core/auth/auth.service';

export interface AddEditUserDialogData {
  mode: 'add' | 'edit';
  user?: UserListItem;
  orgIdOverride?: string | null;
}

@Component({
  selector: 'app-add-edit-user-dialog',
  templateUrl: './add-edit-user-dialog.component.html',
  styleUrls: ['./add-edit-user-dialog.component.css']
})
export class AddEditUserDialogComponent implements OnInit {
  userForm: FormGroup;
  isSubmitting = false;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<AddEditUserDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AddEditUserDialogData,
    private apiService: ApiService,
    private snackBar: MatSnackBar,
    private authService: AuthService
  ) {
    this.userForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      idNumber: [''],
      companyName: [''],
      name: [''],
      surname: [''],
      role: ['user', Validators.required],
      isBillingOwner: [false],
      billingSeatExempt: [false],
      enableTrial: [false],
      twoFactorEnabled: [true],
      inviteAs: ['team_member'],
      team: [false],
    });
  }

  ngOnInit(): void {
    if (this.data.mode === 'edit' && this.data.user) {
      this.userForm.patchValue({
        email: this.data.user.email || '',
        idNumber: this.data.user.idNumber || '',
        companyName: this.data.user.companyName || '',
        name: this.data.user.name || '',
        surname: this.data.user.surname || '',
        role: this.data.user.role || 'user',
        isBillingOwner: this.data.user.isBillingOwner === true,
        billingSeatExempt: this.data.user.billingSeatExempt === true,
        enableTrial: this.data.user.selectedPlanCode === 'INDIVIDUAL_TRIAL',
        twoFactorEnabled: this.data.user.twoFactorEnabled !== false,
        team: this.data.user.team === true,
      });
      // Disable email editing
      this.userForm.get('email')?.disable();
      this.userForm.get('idNumber')?.disable();
      this.userForm.get('companyName')?.disable();
    } else {
      this.userForm.get('role')?.setValue('user', { emitEvent: false });
      this.userForm.get('isBillingOwner')?.setValue(false, { emitEvent: false });
      this.userForm.get('inviteAs')?.setValue('team_member', { emitEvent: false });
      this.userForm.get('enableTrial')?.setValue(false, { emitEvent: false });
      this.userForm.get('name')?.clearValidators();
      this.userForm.get('surname')?.clearValidators();
      this.userForm.get('name')?.updateValueAndValidity({ emitEvent: false });
      this.userForm.get('surname')?.updateValueAndValidity({ emitEvent: false });
    }

    this.userForm.get('inviteAs')?.valueChanges.subscribe((inviteAs) => {
      if (inviteAs !== 'individual') {
        this.userForm.get('enableTrial')?.setValue(false, { emitEvent: false });
      }
    });
    this.userForm.get('team')?.valueChanges.subscribe((team) => {
      if (team === true) {
        this.userForm.get('enableTrial')?.setValue(false, { emitEvent: false });
      }
    });
  }

  get isAddMode(): boolean {
    return this.data.mode === 'add';
  }

  get isTeamAccountSelected(): boolean {
    return this.userForm.get('team')?.value === true;
  }

  get showEnableTrial(): boolean {
    if (this.isAddMode) {
      return this.userForm.get('inviteAs')?.value === 'individual';
    }
    return this.userForm.get('team')?.value === false;
  }

  get isAdminRoleSelected(): boolean {
    return this.userForm.get('role')?.value === 'admin';
  }

  onRoleChanged(nextRole: 'admin' | 'user'): void {
    if (nextRole !== 'admin') {
      this.userForm.get('isBillingOwner')?.setValue(false, { emitEvent: false });
    }
  }

  onSubmit(): void {
    if (this.userForm.invalid) {
      return;
    }

    this.isSubmitting = true;
    const formValue = this.userForm.getRawValue();

    if (this.isAddMode) {
      const inviteAs = String(formValue.inviteAs || 'team_member').toLowerCase();
      this.apiService.createUser({
        email: formValue.email,
        role: formValue.role,
        inviteToTeam: inviteAs === 'individual' ? false : true,
        isBillingOwner: formValue.role === 'admin' && formValue.isBillingOwner === true,
        enableTrial: inviteAs === 'individual' && !!formValue.enableTrial,
      }).subscribe({
        next: () => {
          this.snackBar.open('User created successfully. Account setup email sent', 'Close', { duration: 5000 });
          this.dialogRef.close(true);
        },
        error: (error) => {
          this.isSubmitting = false;
          const errorMessage = this.toApiErrorMessage(error, 'Failed to create user');
          if (!errorMessage) {
            return;
          }
          this.snackBar.open(errorMessage, 'Close', { duration: 5000 });
        }
      });
    } else {
      const updatePayload: any = {
        name: formValue.name,
        surname: formValue.surname,
        role: formValue.role,
        team: !!formValue.team,
        isBillingOwner: formValue.role === 'admin' && formValue.isBillingOwner === true,
        billingSeatExempt: !!formValue.billingSeatExempt,
        enableTrial: formValue.team !== true && !!formValue.enableTrial,
        twoFactorEnabled: !!formValue.twoFactorEnabled,
      };

      this.apiService.updateUser(this.data.user!.userId, updatePayload, this.data.orgIdOverride).subscribe({
        next: (response) => {
          if (response?.requiresTwoFactorReauthentication === true) {
            this.dialogRef.close(false);
            this.snackBar.open(
              'Two-factor authentication was enabled. Please sign in again and complete OTP verification.',
              'Close',
              { duration: 6000 }
            );
            void this.authService.logout();
            return;
          }
          this.snackBar.open('User updated successfully', 'Close', { duration: 3000 });
          this.dialogRef.close(true);
        },
        error: (error) => {
          this.isSubmitting = false;
          const errorMessage = this.toApiErrorMessage(error, 'Failed to update user');
          if (!errorMessage) {
            return;
          }
          this.snackBar.open(errorMessage, 'Close', { duration: 5000 });
        }
      });
    }
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  private toApiErrorMessage(error: any, fallback: string): string | null {
    if (typeof error?.error?.error?.message === 'string' && error.error.error.message.trim()) {
      return error.error.error.message;
    }
    return fallback;
  }
}
